/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { CacheType, createObservableDataAction, IAction, IActionContext, IActionInput, isOboRequest } from '@msdyn365-commerce/core';
import { Employee } from '@msdyn365-commerce/retail-proxy';

/**
 * Input class for the GetEmployeeInput data action.
 */
export class GetEmployeeInput implements IActionInput {
    /**
     * Get Cache Key.
     * @returns - Cache key string.
     */
    public getCacheKey = (): string => 'Employee';

    /**
     * Get Cache type Name.
     * @returns - Cache name string.
     */
    public getCacheObjectType = (): string => 'Employee';

    /**
     * Get Cache type.
     * @returns - CacheType enum.
     */
    public dataCacheType = (): CacheType => 'request';
}

/**
 * CreateInput method for the getEmployee.
 * @returns - Input for data action.
 */
export const createGetEmployeeInput = (): IActionInput => {
    return new GetEmployeeInput();
};

/**
 * The action method for the getEmployee data action.
 * @param input -- The action input.
 * @param ctx -- The action context.
 * @returns -- Employee.
 */
export async function getEmployeeAction(input: GetEmployeeInput, ctx: IActionContext): Promise<Employee | undefined> {
    // For now, we just need the name information of employee. When we need more information, we will invoke EmployeeDataActions.readAsync retail proxy API.
    if (isOboRequest(ctx.requestContext)) {
        const user = ctx.requestContext.user;
        return {
            StaffId: '',
            Name: user?.firstName || user?.name
        };
    }
    return;
}

/**
 * The getEmployee data action.
 * Gets the employee.
 * @returns Employee.
 */
export const getEmployeeActionDataAction = createObservableDataAction<Employee>({
    id: '@msdyn365-commerce-modules/retail-actions/get-employee',
    action: getEmployeeAction as IAction<Employee>,
    input: createGetEmployeeInput
});

export default getEmployeeActionDataAction;
